import React from 'react'
import { Col, Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { DEFAULT_ROLE, ROLES_PER_PAGE, useMeetingUsers, UsersConsent } from './MeetingSummary/hooks/users'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { User } from '../../../models/User'
import { CONSENT_EXCLUDED_ROLES } from './MeetingSummary/ConfigureAssignment/AssignmentRoleNameMapping'
import { Button } from '../../../components/Button/Button'
import { useGetRoomSessionQuery, useOverrideMeetingConsentMutation } from '../../../store/media/media'
import ConsentAvatarLarge from '../../../assets/images/consent-avatart-large.png'
import { ConsentAvatar } from '../../../components/Consent/ConsentAvatar'
import { getUserDisplayName } from './common/aifeatures/utils'
import { ConsentIcon } from '../../../components/Consent/ConsentIcon'
import './ConsentNotAvailable.scss'
import { useZocksAuth, useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { MultiContactConsentList } from '../../../components/Consent/MultiContactConsentList'
import { TrackEvent, useTracking } from './common/tracking'
import { DataRetentionWarning } from '../../../components/Consent/DataRetentionWarning'
import { useConsentManualConfigs } from '../../../components/Consent/useConsentManualConfigs'

export interface ConsentNotAvailableProps {
  loading: boolean
}

const DEFAULT_CONSENT_TEXT = 'Confirm that all clients agreed to this meeting being captured by your AI Assistant.'

export const ConsentNotAvailable: React.FC<ConsentNotAvailableProps> = ({ loading }) => {
  const { hasScope } = useZocksAuth()
  const hasManualConsentManagementAdminScope = hasScope('**', 'consent_management:admin', [])
  const { hasQuota } = useZocksQuota()
  const manualConsentTrackingAvailable = hasQuota('identity.manualConsentTrackingEnabled.value')
  const { evidenceUpload, commentUpload, userConsentManualOverride, consentWarningText } = useConsentManualConfigs()
  const userConsentManualOverrideEnable = ((userConsentManualOverride || hasManualConsentManagementAdminScope) && manualConsentTrackingAvailable) ?? false
  const customConsentText =
    userConsentManualOverrideEnable && consentWarningText?.text
      ? consentWarningText?.enabled
        ? consentWarningText.text
        : DEFAULT_CONSENT_TEXT
      : 'This meeting is locked because one or more clients have not provided consent.'
  const batchClientConsentUpdate = userConsentManualOverrideEnable && !commentUpload?.enabled && !evidenceUpload?.enabled

  if (loading) {
    return (
      <div className='vh-100'>
        <div className='skeleton w-100 h-25' />
      </div>
    )
  }

  return (
    <Container>
      <Row className='p-0'>
        <div className='text-center mt-5'>
          <img src={ConsentAvatarLarge} alt='Consent Avatar' style={{ width: '176px', height: '176px' }} />
        </div>
      </Row>
      <Row>
        <Col lg={8} className='mx-auto'>
          <div className='d-flex flex-column gap-3 justify-content-center align-items-center mt-3'>
            <h4>Verify participant consent</h4>
            <div className='text-center'>
              <span>{customConsentText}</span>
            </div>
            <div>{batchClientConsentUpdate ? 'This will mark all client consent as accepted.' : null}</div>
            <ClientConsentParticipants userConsentManualOverrideEnable={userConsentManualOverrideEnable} />
          </div>
        </Col>
      </Row>
    </Container>
  )
}

interface ClientConsentParticipantsProps {
  userConsentManualOverrideEnable: boolean
}

const ClientConsentParticipants: React.FC<ClientConsentParticipantsProps> = ({ userConsentManualOverrideEnable }) => {
  const accountId = useAppSelector(selectAccountId)
  const { roomId = '', sessionId = '' } = useParams()

  const { commentUpload: commentUploadConfig, evidenceUpload: evidenceUploadConfig } = useConsentManualConfigs()

  const { data: roomSession } = useGetRoomSessionQuery({ accountId, roomId, sessionId })
  const [overrideMeetingConsent, { isLoading }] = useOverrideMeetingConsentMutation()
  const { tracked } = useTracking()
  const { usersByRoles, usersByRolesOnCurrentPage, usersConsent } = useMeetingUsers(roomSession?.data ?? null)

  const showPerClientList = userConsentManualOverrideEnable && ((evidenceUploadConfig?.enabled || commentUploadConfig?.enabled) ?? false)

  const handleOverrideConsent = tracked(
    async () => {
      if (!roomSession?.data) {
        return
      }
      await overrideMeetingConsent({
        accountId,
        roomId,
        sessionId,
      })
    },
    {
      eventName: TrackEvent.CONSENT_CHANGED,
      eventDetails: {
        session_id: sessionId,
        room_id: roomId,
        source: 'locked-per-session',
      },
    },
  )

  if (showPerClientList && evidenceUploadConfig && commentUploadConfig) {
    const clients = usersByRoles[DEFAULT_ROLE]
    return (
      <Container className='d-flex flex-column zocks-gap-2'>
        <Row className='zocks-border-color-gray-300 border border-1 rounded p-3'>
          <Col>
            <MultiContactConsentList
              clients={clients}
              usersConsent={usersConsent}
              evidenceUploadConfig={evidenceUploadConfig}
              commentUploadConfig={commentUploadConfig}
              manualConsentEnabled={userConsentManualOverrideEnable}
              roomId={roomId}
              sessionId={sessionId}
            />
          </Col>
        </Row>
        <DataRetentionWarning />
      </Container>
    )
  }

  const getClientsBody = (usersByRoles: { [key: string]: User[] }, usersConsent: UsersConsent | null) => {
    const clients = (usersByRoles[DEFAULT_ROLE] || []).filter((client) => !client.id.startsWith('speaker_'))

    return (
      <Col xs={12} md={12 / ROLES_PER_PAGE} className='d-flex flex-column p-0'>
        <div className='d-flex flex-row align-items-center participants-container'>
          {clients.map((client, index) => {
            const consent = usersConsent ? (client.roles.every((role) => !CONSENT_EXCLUDED_ROLES.includes(role.id)) ? usersConsent[client.id] : null) : null
            return (
              <div key={client.id} style={{ marginLeft: index > 0 ? '-11px' : '0', zIndex: clients.length - index, position: 'relative' }}>
                <ConsentAvatar name={client.name} consentState={consent?.current?.state} />
              </div>
            )
          })}
          {clients.length > 0 && (
            <OverlayTrigger
              placement='top'
              overlay={
                <Tooltip className='consent-tooltip'>
                  <div className='bg-black text-white rounded p-2'>
                    <div className='d-flex flex-column gap-2'>
                      {clients.map((client) => {
                        const consent = usersConsent ? usersConsent[client.id] : null
                        const shouldShowConsent = client.roles.every((role) => !CONSENT_EXCLUDED_ROLES.includes(role.id))
                        return (
                          <div key={client.id} className='d-flex gap-2 align-items-center' style={{ whiteSpace: 'nowrap' }}>
                            <i className='bi bi-person-fill flex-shrink-0' />
                            <div className='flex-shrink-0'>{getUserDisplayName(client)}</div>
                            {shouldShowConsent && (
                              <div className='flex-shrink-0'>
                                <ConsentIcon contact={client} consent={consent} showText={true} />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </Tooltip>
              }
              popperConfig={{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 20],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      enabled: false,
                    },
                  },
                ],
              }}
            >
              <Button variant='link' className='button-link-small ms-2 p-0'>
                Show all
              </Button>
            </OverlayTrigger>
          )}
        </div>
      </Col>
    )
  }

  return (
    <Container className='zocks-pl-2.75 my-3 d-flex flex-column zocks-gap-2 w-100 mw-100 embedded-container'>
      <Row className='justify-content-center align-items-center zocks-border-color-gray-300 border border-1 rounded p-3'>
        <Col>
          <Row className='d-none d-lg-flex'>{getClientsBody(usersByRolesOnCurrentPage, usersConsent)}</Row>
          <Row className='d-flex d-lg-none'>{getClientsBody(usersByRoles, usersConsent)}</Row>
        </Col>
        {userConsentManualOverrideEnable ? (
          <Col xs='auto'>
            <Button variant='primary' onClick={handleOverrideConsent} disabled={isLoading} loading={isLoading}>
              Confirm consent
            </Button>
          </Col>
        ) : null}
      </Row>
      <DataRetentionWarning />
    </Container>
  )
}
